import React, { useState } from 'react'
import type { TUnderlyingDataExportDto } from '@homepal/api-contract'

import { ParsedData } from 'utils/underlyingData'
import { Popover } from '@mui/material'
import { Sort } from 'utils/types'

import { useTranslation } from 'language/translator'
import { useUtilitiesActions } from 'redux/actions/Utilities'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import LinearProgress from 'components_new/atoms/LinearProgress'
import Text from 'components_new/atoms/Text'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import GenericListItem from 'components_new/molecules/GenericListItem'
import SectionHeader from 'components_new/molecules/SectionHeader'

import { getExportableData } from './utils'

interface Props {
  fetched: boolean
  fileTitle: string
  // only the first rows were fetched; more match the selection
  hasMore: boolean
  // exports every row on the server instead of the fetched ones
  onExportAll?: (fileName: string) => Promise<TUnderlyingDataExportDto>
  onExported: () => void
  parsedData: ParsedData & { sort: Sort }
}

const ExportButton = (props: Props) => {
  const {
    fetched, fileTitle, hasMore, onExportAll, onExported, parsedData
  } = props

  const { t } = useTranslation()
  const { tryExportDataOnServer, tryExportDataXlsx } = useUtilitiesActions()

  const [status, setStatus] = useState<null | 'loading' | 'success'>(null)
  // the rows of an export cut short at what a worksheet holds
  const [truncatedCount, setTruncatedCount] = useState<number | null>(null)

  const handleClose = () => {
    setAnchorEl(null)
    setStatus(null)
    setTruncatedCount(null)
  }

  const handleExport = () => {
    if (!fetched) {
      return
    }

    setStatus('loading')

    if (onExportAll) {
      tryExportDataOnServer(() => onExportAll(fileTitle), (exported) => {
        setStatus(exported ? 'success' : null)
        setTruncatedCount(exported?.truncated ? exported.row_count : null)
      })
    } else {
      const { headers, rows } = getExportableData(parsedData)

      tryExportDataXlsx(headers, rows, fileTitle, () => setStatus('success'))
    }

    onExported()
  }

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  return (
    <>
      <TooltipWrapper
        title={
          status === 'loading'
            ? t('global', 'exporting_to_excel')
            : t('global', 'export_to_excel')
        }
      >
        <IconButton
          disabled={!fetched || status === 'loading'}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
        >
          <Icon fontSize="small" name="Download" />
        </IconButton>
      </TooltipWrapper>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={handleClose}
        open={Boolean(anchorEl)}
        disableScrollLock
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            maxWidth: 300
          }}
        >
          <SectionHeader
            body={t('global', 'export_to_excel_body')}
            size="medium"
            title={t('global', 'export_to_excel')}
          />

          {hasMore ? (
            <Text color="text.secondary" variant="caption">
              {onExportAll
                ? t('global', 'export_all_rows')
                : t('global', 'export_first_rows_only', false, {
                  count: String(parsedData.rows.length)
                })}
            </Text>
          ) : null}

          {truncatedCount !== null ? (
            <Text color="text.secondary" variant="caption">
              {t('global', 'export_truncated', false, {
                count: String(truncatedCount)
              })}
            </Text>
          ) : null}

          <Box sx={{ position: 'relative' }}>
            <GenericListItem
              border={true}
              component="div"
              sx={{
                bgcolor: 'background.default',
                zIndex: 0
              }}
              title={`${fileTitle}.xslx`}
              variant="file"
            />
            {status === 'loading' ? (
              <LinearProgress
                sx={{
                  bottom: 0,
                  position: 'absolute',
                  width: '100%',
                  zIndex: 1
                }}
                variant="query"
              />
            ) : null}
          </Box>
          <Button
            disabled={status === 'loading' || status === 'success'}
            fullWidth={true}
            onClick={handleExport}
            startIcon={
              <Icon
                fontSize="small"
                name={status === 'success' ? 'Check' : 'Download'}
              />
            }
          >
            {status === 'success'
              ? t('global', 'exported')
              : status === 'loading'
                ? onExportAll
                  ? t('global', 'exporting_all_rows')
                  : t('global', 'exporting')
                : t('global', 'export')}
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default ExportButton
